import React, {
	useCallback,
	Fragment,
	useState,
	useContext,
	useEffect,
	useRef,
	useMemo,
} from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { TreeItem } from '@confluence/tree/entry-points/tree-types.ts';
import type { EditorPropertyType } from '@confluence/content-utils/entry-points/EditorPropertyType.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	BLOG_TREE_EXPERIENCE,
	BLOG_TREE_PAGINATION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { useBlogTreeStore } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { addRelayConcreteTypenamesToQueryData } from '@confluence/apollo-relay-interop/entry-points/queryResultTypenames.ts';
import { BLOG_TREE_SEGMENT_KEY } from '@confluence/browser-metrics/entry-points/metric-keys/pageSegmentLoadKey.ts';

import { BlogTreeComponent } from './BlogTreeComponent.tsx';
import { flattenTreeData, getBlogpostNodes, isBlogTreeTruncated } from './dataTransformers.js';
import { PAGE_SIZE, BLOG_STATUSES } from './treeParameters.js';
import { BlogTreeErrorComponent } from './BlogTreeErrorComponent.tsx';
import type {
	BlogTreeRootComponentQuery$data,
	BlogTreeRootComponentQuery$variables,
} from './__generated__/BlogTreeRootComponentQuery.graphql.ts';
import { BlogTreeInitialDataFragment } from './BlogTreeInitialData.fragment.ts';
import type {
	BlogTreeInitialData_query$data,
	BlogTreeInitialData_query$key,
} from './__generated__/BlogTreeInitialData_query.graphql.ts';

export const BlogTreeRootComponentContentFragment: GraphQLTaggedNode = graphql`
	fragment BlogTreeRootComponent_content on Content {
		id
		title
		status
		blank
		properties(keys: ["emoji-title-published", "emoji-title-draft", "editor"]) {
			nodes {
				id
				key
				value
			}
		}
		links {
			webui
		}
		metadata {
			lastModifiedDate
			createdDate
		}
	}
`;

const BlogTreeRootComponentQuery = graphql`
	query BlogTreeRootComponentQuery(
		$spaceKey: String!
		$first: Int
		$endCursor: String
		$statuses: [String]
		$orderBy: String
	) {
		content(
			spaceKey: $spaceKey
			type: "blogpost"
			first: $first
			orderby: $orderBy
			after: $endCursor
			status: $statuses
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				...BlogTreeRootComponent_content @relay(mask: false)
			}
		}
	}
`;

type BlogTreeRootComponentProps = {
	initialDataFragmentRef?: BlogTreeInitialData_query$key;
	spaceKey: string;
	flags: FlagsStateContainer;
	pageTreeFinishedLoading: boolean;
};

type BlogTreeInitialData = {
	readonly content: BlogTreeInitialData_query$data['blogPosts'];
};

const styles = cssMap({
	emptyStateTextWrapper: {
		paddingTop: token('space.075'),
		paddingInlineStart: token('space.250'),
		paddingBottom: token('space.075'),
		color: token('color.text.subtlest'),
	},
});

const i18n = defineMessages({
	noBlogsEmptyStateDescription: {
		id: 'blog-tree.empty-state-description',
		defaultMessage: 'No blogs in this space',
		description:
			'Empty state message displayed in the blog tree when there are no blog posts in the current space',
	},
	paginateErrorFlagDescription: {
		id: 'blog-tree.paginate.failed.description',
		defaultMessage:
			'We encountered a network error while loading more blogs. Refresh the page and try again. If this keeps happening, contact your space administrator.',
		description:
			'An error message that displays in a flag to let the user know that there was a problem while loading more content into their tree',
	},
	paginateErrorFlagTitle: {
		id: 'blog-tree.paginate.failed.title',
		defaultMessage: 'Something went wrong',
		description:
			'Error flag title displayed when loading more blog posts fails during pagination in the blog tree',
	},
});

export interface BlogTreeItem extends TreeItem {
	data: {
		title?: string;
		status?: string;
		blank?: boolean;
		emoji?: string;
		emojiFromProperties?: string;
		webui?: string;
		editorVersion?: EditorPropertyType;
		isSelected?: boolean;
		createdYear?: string;
	};
}

export const BlogTreeRootComponent: React.ComponentType<Omit<BlogTreeRootComponentProps, 'flags'>> =
	withFlags((props: BlogTreeRootComponentProps) => {
		const { initialDataFragmentRef } = props;

		return initialDataFragmentRef ? (
			<BlogTreeRootComponentFromFragment
				{...props}
				initialDataFragmentRef={initialDataFragmentRef}
			/>
		) : (
			<BlogTreeRootComponentInner {...props} />
		);
	});

const BlogTreeRootComponentFromFragment = (
	props: BlogTreeRootComponentProps & { initialDataFragmentRef: BlogTreeInitialData_query$key },
) => {
	const fragmentData = useFragment(BlogTreeInitialDataFragment, props.initialDataFragmentRef);
	const initialData =
		fragmentData.blogPosts != null ? { content: fragmentData.blogPosts } : undefined;

	return <BlogTreeRootComponentInner key={props.spaceKey} {...props} initialData={initialData} />;
};

const BlogTreeRootComponentInner = ({
	flags,
	spaceKey,
	pageTreeFinishedLoading,
	initialData,
}: BlogTreeRootComponentProps & { initialData?: BlogTreeInitialData }) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const intl = useIntl();
	const [fetchMoreError, setFetchMoreError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [shouldForceRefetch, _] = useBlogTreeStore();
	const [useQueryData, setUseQueryData] = useState(false);
	const hasCompleteInitialData =
		initialData?.content?.nodes != null && initialData.content.pageInfo != null;
	const variables = useMemo<BlogTreeRootComponentQuery$variables>(
		() => ({
			spaceKey,
			first: PAGE_SIZE,
			statuses: BLOG_STATUSES,
			orderBy: 'metadata.createdDate desc',
		}),
		[spaceKey],
	);
	const activateQuery = useCallback(() => {
		if (
			!useQueryData &&
			hasCompleteInitialData &&
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
		) {
			// Hand the route's first window to the package query before enabling its
			// cache subscription. This prevents a duplicate request in either client.
			writeQueryInterop({
				query: BlogTreeRootComponentQuery,
				variables,
				data: addRelayConcreteTypenamesToQueryData(BlogTreeRootComponentQuery, initialData),
			});
		}
		setUseQueryData(true);
	}, [useQueryData, hasCompleteInitialData, initialData, variables]);

	const {
		error,
		loading,
		data: queryData,
		fetchMore,
		networkStatus,
		refetch,
	} = useQueryInterop<BlogTreeRootComponentQuery$data, BlogTreeRootComponentQuery$variables>(
		BlogTreeRootComponentQuery,
		{
			skip:
				!useQueryData &&
				hasCompleteInitialData &&
				!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
				expVal('confluence_exp_space_nav_contract', 'isEnabled', false),
			errorPolicy: 'all',
			variables,
			notifyOnNetworkStatusChange: true,
		},
	);
	const data = initialData && !useQueryData ? initialData : queryData;

	const previousRefreshRef = useRef<{
		spaceKey: string;
		shouldForceRefetch: typeof shouldForceRefetch;
	}>();
	useEffect(() => {
		const previous = previousRefreshRef.current;
		if (previous?.spaceKey === spaceKey && previous.shouldForceRefetch === shouldForceRefetch)
			return;

		if (!previous) {
			previousRefreshRef.current = { spaceKey, shouldForceRefetch };
			return;
		}

		if (
			!useQueryData &&
			hasCompleteInitialData &&
			!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
			expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
		) {
			// Subscribe to the seeded query before refetching. Apollo can otherwise
			// discard a refetch that starts while its observable is still skipped.
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			activateQuery();
			return;
		}
		previousRefreshRef.current = { spaceKey, shouldForceRefetch };
		// The external store invalidation requires activating the query and a network refresh.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		activateQuery();
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [shouldForceRefetch, refetch, spaceKey, hasCompleteInitialData, activateQuery, useQueryData]);

	const fetchMoreBlogs = useCallback(async () => {
		try {
			activateQuery();
			const endCursor = data?.content?.pageInfo?.endCursor || '';
			const { errors } = await fetchMore({
				variables: { endCursor },
				updateQuery: (previousQueryResult, { fetchMoreResult }) => {
					if (!fetchMoreResult) return previousQueryResult;

					const mergedData = {
						content: {
							...fetchMoreResult.content,
							nodes: [
								...getBlogpostNodes(previousQueryResult?.content ? previousQueryResult : data),
								...getBlogpostNodes(fetchMoreResult),
							],
							pageInfo: fetchMoreResult?.content?.pageInfo || null,
						},
					};
					if (
						!useQueryData &&
						hasCompleteInitialData &&
						!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_trees_frag_reader') &&
						expVal('confluence_exp_space_nav_contract', 'isEnabled', false)
					) {
						// The first fetchMore captured a skipped Relay query, so it cannot
						// commit the merged window to that query's store subscription itself.
						writeQueryInterop({
							query: BlogTreeRootComponentQuery,
							variables,
							data: addRelayConcreteTypenamesToQueryData(BlogTreeRootComponentQuery, mergedData),
						});
					}
					return mergedData;
				},
			});
			experienceTracker.succeed({
				name: BLOG_TREE_PAGINATION_EXPERIENCE,
			});
			if (errors) {
				// errors is of type GraphQLError[] but we need type ApolloErrorInterop / Error for ErrorDisplay.
				// throwing the error here converts it into an ApolloErrorInterop
				throw errors;
			}
		} catch (e) {
			// fetchMore errors are not surfaced in initial useQuery "error"
			setFetchMoreError(e);
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.paginateErrorFlagTitle),
				description: intl.formatMessage(i18n.paginateErrorFlagDescription),
				isAutoDismiss: true,
			});
		}
	}, [
		fetchMore,
		data,
		flags,
		intl,
		experienceTracker,
		activateQuery,
		useQueryData,
		hasCompleteInitialData,
		variables,
	]);

	if (data && data?.content?.nodes?.length === 0) {
		return (
			<Box xcss={styles.emptyStateTextWrapper}>
				<FormattedMessage {...i18n.noBlogsEmptyStateDescription} />
			</Box>
		);
	}

	const blogTreeData = flattenTreeData(data ? data.content : null);

	return (
		<Fragment>
			<PageSegmentLoadStart metricKey={BLOG_TREE_SEGMENT_KEY} />
			<ExperienceStart name={BLOG_TREE_EXPERIENCE} />
			{error || fetchMoreError ? (
				<Fragment>
					{error && (
						<ErrorDisplay error={error}>
							<ExperienceFailure name={BLOG_TREE_EXPERIENCE} error={error} />
						</ErrorDisplay>
					)}
					{fetchMoreError && (
						<ErrorDisplay error={fetchMoreError}>
							<ExperienceFailure name={BLOG_TREE_PAGINATION_EXPERIENCE} error={fetchMoreError} />
						</ErrorDisplay>
					)}
					<BlogTreeErrorComponent />
				</Fragment>
			) : (
				<BlogTreeComponent
					spaceKey={spaceKey}
					blogTreeData={blogTreeData}
					shouldShowLoadMoreButton={data ? isBlogTreeTruncated(data.content) : false}
					fetchMoreBlogs={fetchMoreBlogs}
					networkStatus={networkStatus}
					loading={loading}
					pageTreeFinishedLoading={pageTreeFinishedLoading}
				/>
			)}
		</Fragment>
	);
};
